// Client-side plan resolution. Spec 8.2.
//
// The worker hands back plans with raw leg timings and segment IDs. This
// file substitutes the user's own walk numbers, recomputes catchability,
// re-ranks, and drops plans that have expired. It is a few milliseconds of
// arithmetic, it works with no signal, and it means editing a walk time
// updates the list instantly with no round trip.
import {
  catchability,
  segmentFor,
  segmentSeconds,
  type Catchability,
  type Profile,
  type WalkSegment,
} from '../../shared/segments';

export interface Leg {
  kind: 'walk' | 'tube' | 'nationalrail' | 'overground' | 'bus' | 'dlr';
  line?: string;
  operator?: string;
  dep?: string;
  arr?: string;
  from?: string;
  to: string;
  minutes: number;
  note?: string;
  platform?: string;
  segmentId?: string;
  source?: 'darwin' | 'timetable';
  onTime?: boolean;
  severity?: Severity;
  delayMinutes?: number;
  reason?: string;
}

export type Severity = 'ok' | 'minor' | 'major' | 'broken' | 'unknown';

/**
 * Spec 5.7: severity does not sort the list, it adds minutes, so plans
 * still sort by realistic arrival time. `broken` being Infinity means such
 * a plan sorts itself off the end with no special case, which is why
 * walking floats to the top when both tube lines are suspended.
 */
export const PENALTY_MINUTES: Record<Severity, number> = {
  ok: 0,
  minor: 4,
  unknown: 6,
  major: 15,
  broken: Infinity,
};

export interface Plan {
  id: string;
  family: string;
  approach: string;
  severity?: Severity;
  variancePenaltyMinutes?: number;
  band?: { fromIso: string; toIso: string };
  canBeHeadline?: boolean;
  leaveBy: string;
  catchability: Catchability;
  arrivesAt: string;
  departsAtIso: string;
  arrivesAtIso: string;
  firstDepartureIso?: string;
  totalMinutes: number;
  legs: Leg[];
  signals: { severity: string; headline: string; detail?: string }[];
}

export interface HomePayload {
  generatedAt: string;
  origin: { label: string; confidence: string; accuracyM?: number };
  destination: { id: string; label: string; modelling: string; chosenBy: string };
  headline: string;
  escalation: {
    state: 'none' | 'soft' | 'strong' | 'blocked';
    roadsViable: boolean;
    deltaMinutes: number;
    ratio?: number;
    incidentKey?: string;
    message?: string;
    links?: { name: string; url: string }[];
  };
  roadHealth?: Severity;
  approachHealth?: Record<string, string>;
  plans: Plan[];
  degraded?: string;
  degradedReason?: string;
}

export interface ResolvedLeg extends Leg {
  /** After the user's own numbers and pace dial are applied. */
  resolvedMinutes: number;
  segment?: WalkSegment;
}

export interface ResolvedPlan extends Plan {
  legs: ResolvedLeg[];
  /** Total walking before the first scheduled departure. */
  walkMinutesBeforeDeparture: number;
  /** Minutes from now until you must be moving. Can be negative. */
  leaveInMinutes: number;
  /** Arrival with the user's exit walk added on. The time we show. */
  effectiveArrival: Date;
  /** For bus plans, the honest range rather than a false point. Spec 5.8. */
  effectiveBand?: { from: Date; to: Date };
  /**
   * Effective arrival plus the health penalty. Never displayed, only
   * sorted on, so a disrupted plan sinks without the clock appearing to
   * lie about when it would get you there.
   */
  rankingArrival: number;
  expired: boolean;
}

const minutesBetween = (a: Date, b: Date) => (b.getTime() - a.getTime()) / 60_000;

/**
 * Spec 5.5 note: accessPenalty is a placeholder until step-free data lands
 * in Phase 3. It stays in the signature so the call sites do not change.
 */
const accessPenalty = (_plan: Plan, profile: Profile) => (profile.stepFreeRequired ? 2 : 0);

export function resolvePlan(
  plan: Plan,
  segments: Map<string, WalkSegment>,
  profile: Profile,
  now: Date
): ResolvedPlan {
  const legs: ResolvedLeg[] = plan.legs.map((leg) => {
    if (leg.kind !== 'walk' || !leg.segmentId) return { ...leg, resolvedMinutes: leg.minutes };

    const seg = segmentFor(
      leg.segmentId,
      leg.note ?? `${leg.from ?? ''} to ${leg.to}`,
      leg.segmentId.startsWith('ix:') ? 'interchange'
        : leg.segmentId.startsWith('exit:') ? 'exit'
        : 'street',
      segments
    );

    // A street walk the planner already measured: our seed is its number,
    // unless the user has said otherwise. An interchange the planner does
    // not model at all: our seed is the only number there is.
    const effective: WalkSegment =
      seg.kind === 'street' && seg.seedSeconds === 0
        ? { ...seg, seedSeconds: leg.minutes * 60 }
        : seg;

    return {
      ...leg,
      segment: effective,
      resolvedMinutes: segmentSeconds(effective, now, profile) / 60,
    };
  });

  const firstDeparture = plan.firstDepartureIso ? new Date(plan.firstDepartureIso) : null;

  // Walking that happens before the first scheduled thing leaves is the
  // walking you have to actually do in time. Later walks affect arrival,
  // not whether you catch the train.
  const firstTransitIndex = legs.findIndex((l) => l.kind !== 'walk');
  const walkMinutesBeforeDeparture = legs
    .slice(0, firstTransitIndex === -1 ? legs.length : firstTransitIndex)
    .reduce((sum, l) => sum + l.resolvedMinutes, 0);

  const available = firstDeparture ? minutesBetween(now, firstDeparture) : Infinity;

  const catch_ = firstDeparture
    ? catchability(walkMinutesBeforeDeparture, accessPenalty(plan, profile), available, profile)
    : 'comfortable';

  // Walking after the last transit leg pushes your real arrival later than
  // the planner's. This is the honest number, so it is what we rank on.
  const lastTransitIndex = legs.map((l) => l.kind !== 'walk').lastIndexOf(true);
  const trailingWalk = legs
    .slice(lastTransitIndex + 1)
    .reduce((sum, l) => sum + l.resolvedMinutes, 0);

  const effectiveArrival = new Date(
    new Date(plan.arrivesAtIso).getTime() + trailingWalk * 60_000
  );

  const needed = walkMinutesBeforeDeparture + accessPenalty(plan, profile) + profile.bufferMinutes;

  // Two separate penalties, for two separate reasons. Health says the line
  // is in trouble (5.7). Variance says this leg's time is a median rather
  // than a prediction, and your actual experience is the tail (5.8).
  const penalty = PENALTY_MINUTES[plan.severity ?? 'ok'] + (plan.variancePenaltyMinutes ?? 0);

  // The band moves with the exit walk too, otherwise the top of the range
  // would be honest and the bottom would not.
  const band = plan.band
    ? {
        from: new Date(new Date(plan.band.fromIso).getTime() + trailingWalk * 60_000),
        to: new Date(new Date(plan.band.toIso).getTime() + trailingWalk * 60_000),
      }
    : undefined;

  return {
    ...plan,
    legs,
    catchability: catch_,
    walkMinutesBeforeDeparture,
    leaveInMinutes: available === Infinity ? Infinity : available - needed,
    effectiveArrival,
    effectiveBand: band,
    rankingArrival: effectiveArrival.getTime() + penalty * 60_000,
    expired: catch_ === 'missed',
  };
}

/**
 * Spec 5.6: rank by predicted arrival, earliest first, then diversify.
 * Spec 5.5: drop missed plans, keep tight ones but mark them, because
 * sometimes you do want to run for it.
 */
export function resolveAndRank(
  payload: HomePayload,
  segments: Map<string, WalkSegment>,
  profile: Profile,
  now: Date
): ResolvedPlan[] {
  const resolved = payload.plans
    .map((p) => resolvePlan(p, segments, profile, now))
    // A broken plan has an infinite penalty, so it has already sorted
    // itself off the end. Drop it rather than render Invalid Date.
    .filter((p) => !p.expired && Number.isFinite(p.rankingArrival))
    .sort((a, b) => a.rankingArrival - b.rankingArrival);

  // At most two plans per family, so you do not get five consecutive SWR
  // departures. Spec 5.6.
  //
  // This is a hard cap, not a preference. Topping the list back up to five
  // with a third SWR departure would be exactly the thing the rule exists
  // to prevent, so a thin evening shows four cards rather than five.
  const perFamily = new Map<string, number>();
  const diverse: ResolvedPlan[] = [];
  for (const p of resolved) {
    const n = perFamily.get(p.family) ?? 0;
    if (n < 2) {
      perFamily.set(p.family, n + 1);
      diverse.push(p);
    }
  }

  // Always include at least one plan from each viable approach, so the
  // fallback is visible even when one approach is winning. Spec 5.6.
  if (payload.destination.modelling === 'richmond') {
    const shown = new Set(diverse.slice(0, 5).map((p) => p.approach));
    for (const p of resolved) {
      if (!shown.has(p.approach) && p.approach !== 'none') {
        shown.add(p.approach);
        if (!diverse.includes(p)) diverse.push(p);
      }
    }
    diverse.sort((a, b) => a.rankingArrival - b.rankingArrival);
  }

  // Spec 5.8: treat bus as its own approach for diversity. `strand-bus-wat`
  // still depends on approach A once it reaches Waterloo, but the leg that
  // gets you there fails independently of the tube, and that is the whole
  // point of including it. So always allow one bus plan in when the
  // corridor is degraded, because it is the option with no cliff.
  const top = diverse.slice(0, 5);
  const corridorDegraded = resolved.some(
    (p) => p.severity === 'major' || p.severity === 'broken'
  );
  if (corridorDegraded && !top.some((p) => p.legs.some((l) => l.kind === 'bus'))) {
    const bus = resolved.find((p) => p.legs.some((l) => l.kind === 'bus'));
    if (bus) return [...top.slice(0, 4), bus].sort((a, b) => a.rankingArrival - b.rankingArrival);
  }
  return top;
}

/**
 * Spec 5.8: never let a bus plan be the headline unless every rail plan is
 * major or worse. The one sentence at the top of the screen should be
 * something you can rely on, and a bus arrival time is the least
 * trustworthy number on the screen.
 */
export const headlinePlan = (plans: ResolvedPlan[]): ResolvedPlan | undefined =>
  plans.find((p) => p.canBeHeadline !== false) ?? plans[0];

export const fmtTime = (d: Date) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

export function fmtCountdown(minutes: number): string {
  if (!Number.isFinite(minutes)) return 'any time';
  const m = Math.floor(Math.abs(minutes));
  const s = Math.floor((Math.abs(minutes) - m) * 60);
  const body = `${m}:${String(s).padStart(2, '0')}`;
  return minutes < 0 ? `-${body}` : body;
}
